// ---------------------------------------------------------------------------
// repo-telemetry — transport-agnostic observability sink for
// `useKg().repo` call records.
//
// Handoff v4 §4.3 — prerequisite for prod `VITE_KG_BROWSER=1` flag flip.
// Ships infrastructure; real Sentry / PostHog SDK wiring lands as a
// second PR once those services are configured in the Studio deploy
// environment.
//
// Design:
//   - Consumes the same `KgCallRecord` shape already produced by
//     `wrapRepoWithDevtools` (PR #73). kg-provider.tsx composes devtools
//     sink + telemetry sink into one `onCall` callback.
//   - `TelemetrySink` interface: `track(entry)` + optional `flush()`.
//   - Pluggable sinks: `NoOpTelemetrySink` (default, no env), `BeaconTelemetrySink`
//     (navigator.sendBeacon POST when `VITE_KG_TELEMETRY_ENDPOINT` set),
//     `ComposeSink` (fan-out to multiple sinks).
//   - Batching: 50-entry buffer or 30s interval, whichever first. Final
//     flush on pagehide so we don't lose tail events on tab close.
//   - Payload shape is server-agnostic JSON — consumer service chooses
//     how to translate to Sentry / PostHog / custom pipeline.
//
// Consumer wire-up (in kg-provider.tsx):
//
//   const telemetry = createTelemetrySinkFromEnv({
//     appId: 'cogo-studio',
//     shapeSha: () => repoRef.current?.versionInfo().shapeSha ?? 'unknown',
//   })
//   const onCall = composeSinks(
//     (e) => useDevToolsStore.getState().pushKgCall(e),
//     (e) => telemetry.track(e),
//   )
//   repoRef.current = wrapRepoWithDevtools(raw, onCall)
// ---------------------------------------------------------------------------

import type { KgCallRecord } from '../../devtools/store/devtools-store'

// ——— Types ————————————————————————————————————————————————————————

export type TelemetryEvent = Omit<KgCallRecord, 'id' | 'timestamp'>

export interface TelemetrySink {
  track(event: TelemetryEvent): void
  /** Force any buffered events to be delivered. */
  flush(): void
  /** Release resources (remove pagehide listener, cancel interval). */
  dispose(): void
}

export interface TelemetryConfig {
  /** Logical app id for the payload (e.g. "cogo-studio"). */
  appId: string
  /** Endpoint to beacon. If null/undefined, a no-op sink is returned. */
  endpoint?: string | null
  /**
   * Per-request Schema-pointer so the server can correlate latency
   * against a specific ontology build. Called per flush (not per track)
   * so the cost stays amortized.
   */
  shapeSha: () => string | undefined
  /**
   * Optional stable session id. When omitted, a per-mount random id is
   * generated (so server can group events from the same tab).
   */
  sessionId?: string
  /** Max buffered events before auto-flush. Defaults to 50. */
  maxBuffer?: number
  /** Max seconds to hold buffered events. Defaults to 30. */
  flushIntervalMs?: number
}

// ——— No-op sink ——————————————————————————————————————————————————

export const noOpTelemetrySink: TelemetrySink = {
  track() {},
  flush() {},
  dispose() {},
}

// ——— Beacon sink ——————————————————————————————————————————————————

type FlushTarget = (payload: string) => boolean | void

/**
 * Create a `TelemetrySink` that buffers `KgCallRecord` events and ships
 * them to `endpoint` via `navigator.sendBeacon` (with `fetch` fallback).
 *
 * Exported `createBeaconTelemetrySink` bypasses the env gate — for tests
 * + explicit caller control. Most callers should use
 * `createTelemetrySinkFromEnv()`.
 */
export function createBeaconTelemetrySink(
  config: TelemetryConfig & { endpoint: string },
  transport?: FlushTarget,
): TelemetrySink {
  const {
    appId,
    endpoint,
    shapeSha,
    sessionId = randomSessionId(),
    maxBuffer = 50,
    flushIntervalMs = 30_000,
  } = config

  const buffer: TelemetryEvent[] = []
  let intervalId: ReturnType<typeof setInterval> | null = null
  let disposed = false

  const send = transport ?? defaultBeaconTransport(endpoint)

  const flushNow = (): void => {
    if (buffer.length === 0) return
    const events = buffer.splice(0)
    const payload = JSON.stringify({
      appId,
      sessionId,
      shapeSha: shapeSha(),
      clientTimestamp: Date.now(),
      events,
    })
    send(payload)
  }

  const scheduleFlushInterval = (): void => {
    if (intervalId !== null) return
    if (typeof setInterval !== 'function') return
    intervalId = setInterval(flushNow, flushIntervalMs)
  }

  const cancelFlushInterval = (): void => {
    if (intervalId === null) return
    clearInterval(intervalId)
    intervalId = null
  }

  const pagehideHandler = (): void => {
    flushNow()
  }

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', pagehideHandler)
  }
  scheduleFlushInterval()

  return {
    track(event) {
      if (disposed) return
      buffer.push(event)
      if (buffer.length >= maxBuffer) flushNow()
    },
    flush: flushNow,
    dispose() {
      if (disposed) return
      disposed = true
      flushNow()
      cancelFlushInterval()
      if (
        typeof window !== 'undefined' &&
        typeof window.removeEventListener === 'function'
      ) {
        window.removeEventListener('pagehide', pagehideHandler)
      }
    },
  }
}

function defaultBeaconTransport(endpoint: string): FlushTarget {
  return (payload) => {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const ok = navigator.sendBeacon(
          endpoint,
          new Blob([payload], { type: 'application/json' }),
        )
        if (ok) return true
      } catch {
        /* fall through to fetch */
      }
    }
    if (typeof fetch === 'function') {
      void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {
        /* best-effort; drop on failure */
      })
      return true
    }
    return false
  }
}

function randomSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `kg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

// ——— Compose ——————————————————————————————————————————————————————

/**
 * Compose multiple sinks into a single `track` callback. Errors thrown
 * by one sink do not interrupt other sinks (observability must not
 * bring down the app).
 */
export function composeSinks(
  ...sinks: Array<(event: TelemetryEvent) => void>
): (event: TelemetryEvent) => void {
  return (event) => {
    for (const sink of sinks) {
      try {
        sink(event)
      } catch {
        /* swallow — observability must never throw into Repository call */
      }
    }
  }
}

// ——— Env-gated factory ————————————————————————————————————————————

/**
 * Return a live sink when `VITE_KG_TELEMETRY_ENDPOINT` is set; otherwise
 * return the no-op sink. Consumers call `track()` unconditionally — the
 * sink itself is the feature flag.
 */
export function createTelemetrySinkFromEnv(
  args: Pick<TelemetryConfig, 'appId' | 'shapeSha' | 'sessionId'>,
  endpoint?: string,
): TelemetrySink {
  const resolved =
    endpoint ??
    (typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_KG_TELEMETRY_ENDPOINT as string | undefined)
      : undefined)
  if (!resolved) return noOpTelemetrySink
  return createBeaconTelemetrySink({ ...args, endpoint: resolved })
}

// ——— Sentry sink ——————————————————————————————————————————————————
//
// Emits KG call records as Sentry breadcrumbs (not errors — KG calls are
// not failures). On error-typed records (repo call that threw), also
// captures a scoped message so Sentry groups latency anomalies with the
// surrounding request trace.
//
// Resolution order:
//   1. Explicit `sentry` handle passed to the factory
//   2. `window.Sentry` global (CDN script-tag deployment — common for
//      frontend telemetry so SDK upgrades don't require a rebuild)
// When neither is available, returns `noOpTelemetrySink` — same "sink
// IS the feature flag" contract as the beacon variant.
//
// This layer is SDK-version-agnostic: it uses only `addBreadcrumb` +
// `captureMessage`, both present in every Sentry browser SDK since v5.
// Tests inject a fake handle; prod deploys load the real SDK via CDN or
// npm (outside this file's scope).

export interface SentryLike {
  addBreadcrumb?: (breadcrumb: {
    category?: string
    message?: string
    level?: 'info' | 'warning' | 'error'
    data?: Record<string, unknown>
  }) => void
  captureMessage?: (
    message: string,
    level?: 'info' | 'warning' | 'error',
  ) => void
}

export interface SentryTelemetryConfig {
  appId: string
  shapeSha: () => string | undefined
  sessionId?: string
  /** Inject a custom Sentry handle (tests, SSR, npm-imported SDK). */
  sentry?: SentryLike
}

/**
 * Resolve a Sentry-compatible handle. Prefers the injected handle, falls
 * back to `window.Sentry`. Returns `null` when neither is available — the
 * caller treats this as "no Sentry wiring" and returns the no-op sink.
 */
function resolveSentry(injected?: SentryLike): SentryLike | null {
  if (injected) return injected
  if (typeof window !== 'undefined') {
    const w = window as unknown as { Sentry?: SentryLike }
    if (w.Sentry && typeof w.Sentry === 'object') return w.Sentry
  }
  return null
}

export function createSentryTelemetrySink(
  config: SentryTelemetryConfig,
): TelemetrySink {
  const sentry = resolveSentry(config.sentry)
  if (!sentry) return noOpTelemetrySink
  const { appId, shapeSha, sessionId = randomSessionId() } = config
  let disposed = false

  return {
    track(event) {
      if (disposed) return
      const isError = typeof event.error === 'string' && event.error.length > 0
      const level: 'info' | 'warning' | 'error' = isError ? 'error' : 'info'
      // Every call is a breadcrumb — low-noise, high-context.
      sentry.addBreadcrumb?.({
        category: 'kg.repo',
        message: event.method,
        level,
        data: {
          appId,
          sessionId,
          shapeSha: shapeSha(),
          durationMs: event.durationMs,
          source: event.source,
          cacheHit: event.cacheHit,
          error: event.error,
        },
      })
      // Errors additionally capture a scoped message so Sentry groups
      // them. Keeps non-error traffic out of the issues list.
      if (isError) {
        sentry.captureMessage?.(
          `kg.repo ${event.method} failed: ${event.error}`,
          'error',
        )
      }
    },
    flush() {
      /* Sentry batches internally; no client-side flush surface. */
    },
    dispose() {
      disposed = true
    },
  }
}

/**
 * Env-gated factory: enabled when `VITE_SENTRY_DSN` is set AND a Sentry
 * handle is resolvable (injected or `window.Sentry`). Presence of the DSN
 * env without a loaded SDK ⇒ noop (logged once in DEV).
 */
export function createSentrySinkFromEnv(
  args: Pick<SentryTelemetryConfig, 'appId' | 'shapeSha' | 'sessionId' | 'sentry'>,
  dsn?: string,
): TelemetrySink {
  const resolved =
    dsn ??
    (typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_SENTRY_DSN as string | undefined)
      : undefined)
  if (!resolved) return noOpTelemetrySink
  return createSentryTelemetrySink(args)
}

// ——— PostHog sink —————————————————————————————————————————————————
//
// Emits KG call records as PostHog `capture()` analytics events. Unlike
// Sentry, every call is a first-class event — latency distributions, call
// volume by method, etc., all land in PostHog dashboards. PostHog batches
// internally; client-side `flush()` is a no-op.
//
// Resolution: explicit `posthog` handle → `window.posthog` global. When
// neither is available, returns `noOpTelemetrySink`.

export interface PostHogLike {
  capture?: (
    eventName: string,
    properties?: Record<string, unknown>,
  ) => void
}

export interface PostHogTelemetryConfig {
  appId: string
  shapeSha: () => string | undefined
  sessionId?: string
  /** Event name prefix (e.g. 'kg.repo' → 'kg.repo.searchComponents'). */
  eventPrefix?: string
  /** Inject a custom PostHog handle (tests, npm-imported SDK). */
  posthog?: PostHogLike
}

function resolvePostHog(injected?: PostHogLike): PostHogLike | null {
  if (injected) return injected
  if (typeof window !== 'undefined') {
    const w = window as unknown as { posthog?: PostHogLike }
    if (w.posthog && typeof w.posthog === 'object') return w.posthog
  }
  return null
}

export function createPostHogTelemetrySink(
  config: PostHogTelemetryConfig,
): TelemetrySink {
  const posthog = resolvePostHog(config.posthog)
  if (!posthog) return noOpTelemetrySink
  const {
    appId,
    shapeSha,
    sessionId = randomSessionId(),
    eventPrefix = 'kg.repo',
  } = config
  let disposed = false

  return {
    track(event) {
      if (disposed) return
      posthog.capture?.(`${eventPrefix}.${event.method}`, {
        appId,
        sessionId,
        shapeSha: shapeSha(),
        durationMs: event.durationMs,
        source: event.source,
        cacheHit: event.cacheHit,
        error: event.error,
      })
    },
    flush() {
      /* PostHog batches internally; no explicit flush. */
    },
    dispose() {
      disposed = true
    },
  }
}

/**
 * Env-gated factory: enabled when `VITE_POSTHOG_KEY` is set AND a PostHog
 * handle is resolvable (injected or `window.posthog`).
 */
export function createPostHogSinkFromEnv(
  args: Pick<
    PostHogTelemetryConfig,
    'appId' | 'shapeSha' | 'sessionId' | 'eventPrefix' | 'posthog'
  >,
  apiKey?: string,
): TelemetrySink {
  const resolved =
    apiKey ??
    (typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_POSTHOG_KEY as string | undefined)
      : undefined)
  if (!resolved) return noOpTelemetrySink
  return createPostHogTelemetrySink(args)
}
